// src/pages/PricesPage.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createAuditedClient } from '../utils/auditClient';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useCountry } from '../context/CountryContext';
import { Button } from '../ui';
import { Plus, RefreshCcw, Save, Trash2, Pencil } from 'lucide-react';

const client = createAuditedClient('PricesPage');

/* ======================= GraphQL ======================= */

const ListCars = /* GraphQL */ `
  query ListCars($limit: Int, $nextToken: String, $filter: ModelCarFilterInput) {
    listCars(limit: $limit, nextToken: $nextToken, filter: $filter) {
      items {
        id
        CompanyId
        name
        plate
        bodyType
        startingPrice
        fuelType
        transmission
      }
      nextToken
    }
  }
`;
// PricesPage.jsx — GraphQL selections (add insuranceTiers everywhere)

const AddonsByCompanyByCountry = /* GraphQL */ `
  query AddonsByCompanyByCountry(
    $CompanyId: String!
    $countryCode: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $limit: Int
    $nextToken: String
  ) {
    addonsByCompanyByCountry(
      CompanyId: $CompanyId
      countryCode: $countryCode
      sortDirection: $sortDirection
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        CompanyId
        countryCode
        currency

        insuranceTiers
        insurancePerDay_1_5
        insurancePerDay_6_plus

        boosterSeatPerDay
        boosterSeatMax
        infantSeatPerDay
        infantSeatMax
        babySeatPerDay
        babySeatMax
        snowChainsPerDay
        snowChainsMax
        extraDriverPerDay
        extraDriverMax

        updatedAt
      }
      nextToken
    }
  }
`;

const CreateAddonsPricing = /* GraphQL */ `
  mutation CreateAddonsPricing($input: CreateAddonsPricingInput!) {
    createAddonsPricing(input: $input) {
      id
      CompanyId
      countryCode
      currency

      insuranceTiers
      insurancePerDay_1_5
      insurancePerDay_6_plus

      boosterSeatPerDay
      boosterSeatMax
      infantSeatPerDay
      infantSeatMax
      babySeatPerDay
      babySeatMax
      snowChainsPerDay
      snowChainsMax
      extraDriverPerDay
      extraDriverMax

      updatedAt
    }
  }
`;

const UpdateAddonsPricing = /* GraphQL */ `
  mutation UpdateAddonsPricing($input: UpdateAddonsPricingInput!) {
    updateAddonsPricing(input: $input) {
      id
      CompanyId
      countryCode
      currency

      insuranceTiers
      insurancePerDay_1_5
      insurancePerDay_6_plus

      boosterSeatPerDay
      boosterSeatMax
      infantSeatPerDay
      infantSeatMax
      babySeatPerDay
      babySeatMax
      snowChainsPerDay
      snowChainsMax
      extraDriverPerDay
      extraDriverMax

      updatedAt
    }
  }
`;



const PriceFramesByCompanyByStart = /* GraphQL */ `
  query PriceFramesByCompanyByStart(
    $CompanyId: String!
    $startDate: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $limit: Int
    $nextToken: String
  ) {
    priceFramesByCompanyByStart(
      CompanyId: $CompanyId
      startDate: $startDate
      sortDirection: $sortDirection
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        CompanyId
        name
        startDate
        endDate
        currency
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const CarPricesByCompanyByFrame = /* GraphQL */ `
  query CarPricesByCompanyByFrame(
    $CompanyId: String!
    $frameIdCarId: ModelCarPriceCarPricesByCompanyByFrameCompositeKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelCarPriceFilterInput
    $limit: Int
    $nextToken: String
  ) {
    carPricesByCompanyByFrame(
      CompanyId: $CompanyId
      frameIdCarId: $frameIdCarId
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        CompanyId
        frameId
        carId
        p1_2
        p3_4
        p5_10
        p11_20
        p20_plus
        updatedAt
      }
      nextToken
    }
  }
`;

const CreatePriceFrame = /* GraphQL */ `
  mutation CreatePriceFrame($input: CreatePriceFrameInput!) {
    createPriceFrame(input: $input) {
      id
      CompanyId
      name
      startDate
      endDate
      currency
      createdAt
      updatedAt
    }
  }
`;

const UpdatePriceFrame = /* GraphQL */ `
  mutation UpdatePriceFrame($input: UpdatePriceFrameInput!) {
    updatePriceFrame(input: $input) {
      id
      CompanyId
      name
      startDate
      endDate
      currency
      createdAt
      updatedAt
    }
  }
`;

const DeletePriceFrame = /* GraphQL */ `
  mutation DeletePriceFrame($input: DeletePriceFrameInput!) {
    deletePriceFrame(input: $input) {
      id
    }
  }
`;

const CreateCarPrice = /* GraphQL */ `
  mutation CreateCarPrice($input: CreateCarPriceInput!) {
    createCarPrice(input: $input) {
      id
      CompanyId
      frameId
      carId
      p1_2
      p3_4
      p5_10
      p11_20
      p20_plus
      updatedAt
    }
  }
`;

const UpdateCarPrice = /* GraphQL */ `
  mutation UpdateCarPrice($input: UpdateCarPriceInput!) {
    updateCarPrice(input: $input) {
      id
      CompanyId
      frameId
      carId
      p1_2
      p3_4
      p5_10
      p11_20
      p20_plus
      updatedAt
    }
  }
`;

const DeleteCarPrice = /* GraphQL */ `
  mutation DeleteCarPrice($input: DeleteCarPriceInput!) {
    deleteCarPrice(input: $input) {
      id
    }
  }
`;

/* ======================= Helpers ======================= */

function toIntOrNull(v) {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? Math.trunc(n) : null;
}

function toFloatOrNull(v) {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

function parseTiers(raw) {
    try {
        const arr = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (!Array.isArray(arr)) return null;
        return arr.map((t) => ({
            minDays: toIntOrNull(t?.minDays) ?? 1,
            maxDays: t?.maxDays === null ? null : toIntOrNull(t?.maxDays),
            perDay: t?.perDay ?? null,
        }));
    } catch {
        return null;
    }
}

function normalizeTiersForSave(tiers) {
    const cleaned = (tiers || [])
        .map((t) => ({
            minDays: toIntOrNull(t.minDays),
            maxDays: t.maxDays === null ? null : toIntOrNull(t.maxDays),
            perDay: toFloatOrNull(t.perDay),
        }))
        .filter((t) => Number.isInteger(t.minDays) && t.minDays >= 1 && Number.isFinite(t.perDay) && t.perDay >= 0)
        .sort((a, b) => a.minDays - b.minDays);

    // basic validation: no overlaps, only last can be open-ended
    for (let i = 0; i < cleaned.length; i++) {
        const cur = cleaned[i];
        if (cur.maxDays != null && cur.maxDays < cur.minDays) return { ok: false, tiers: cleaned, error: 'Insurance tier: maxDays < minDays.' };
        if (cur.maxDays == null && i !== cleaned.length - 1) return { ok: false, tiers: cleaned, error: 'Insurance tier: only the last tier can be open-ended.' };
        if (i > 0) {
            const prev = cleaned[i - 1];
            if (prev.maxDays == null) return { ok: false, tiers: cleaned, error: 'Insurance tier: open-ended tier must be last.' };
            if (cur.minDays <= prev.maxDays) return { ok: false, tiers: cleaned, error: 'Insurance tier: overlapping ranges.' };
        }
    }

    return { ok: cleaned.length > 0, tiers: cleaned, error: cleaned.length > 0 ? null : 'Add at least one valid insurance tier.' };
}
async function smartAuthMode() {
    const s = await fetchAuthSession();
    if (s?.tokens?.accessToken) return 'userPool';
    if (s?.credentials) return 'iam';
    return 'iam';
}

const BASE_YEAR = 2000; // dummy year for recurring frames (leap year => Feb 29 safe)

function pad2(n) {
    return String(n).padStart(2, '0');
}

function todayDDMM() {
    const d = new Date();
    return `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}`;
}

function isoToDDMM(iso) {
    if (!iso) return '';
    const s = String(iso);
    const mm = s.slice(5, 7);
    const dd = s.slice(8, 10);
    return `${dd}-${mm}`;
}

function ddmmToISO(ddmm) {
    const [dd, mm] = String(ddmm || '').split('-');
    if (!dd || !mm) return '';
    return `${BASE_YEAR}-${mm}-${dd}`;
}

function ddmmInRange(ddmm, startDDMM, endDDMM) {
    if (!ddmm || !startDDMM || !endDDMM) return false;

    const toKey = (x) => {
        const [dd, mm] = String(x).split('-');
        return `${mm}-${dd}`; // compare as MM-DD
    };

    const cur = toKey(ddmm);
    const s = toKey(startDDMM);
    const e = toKey(endDDMM);

    if (s <= e) return cur >= s && cur <= e;
    return cur >= s || cur <= e;
}

function compareDDMM(a, b) {
    const toKey = (x) => {
        const [dd, mm] = String(x || '').split('-');
        return `${mm}-${dd}`;
    };
    return toKey(a).localeCompare(toKey(b));
}

function defaultFrameLabel(startDDMM, endDDMM) {
    if (!startDDMM || !endDDMM) return 'Seasonal frame';
    return `Frame ${startDDMM} → ${endDDMM}`;
}


function safeNum(v) {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

function rangesLabel(key) {
    switch (key) {
        case 'p1_2':
            return '1–2 days';
        case 'p3_4':
            return '3–4 days';
        case 'p5_10':
            return '5–10 days';
        case 'p11_20':
            return '11–20 days';
        case 'p20_plus':
            return '21+ days';
        default:
            return key;
    }
}


function daysInMonth(mm) {
    const m = Number(mm);
    if (!m || m < 1 || m > 12) return 31;
    return new Date(BASE_YEAR, m, 0).getDate();
}

const BUCKETS = ['p1_2', 'p3_4', 'p5_10', 'p11_20', 'p20_plus'];

/* ---------- shared styles (match CarsPage vibe) ---------- */

function cardStyle() {
    return {
        border: '1px solid #e5e7eb',
        borderRadius: 12,
        padding: 14,
        background: '#ffffff',
        boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
    };
}

const labelStyle = {
    fontSize: 13,
    color: '#374151',
    fontWeight: 600,
    marginBottom: 6,
};

const inputStyle = {
    width: '100%',
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid #d1d5db',
    fontSize: 13,
    outline: 'none',
    boxSizing: 'border-box',
    background: '#f9fafb',
};

const pillStyle = (bg, color, border) => ({
    display: 'inline-block',
    padding: '4px 10px',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    borderRadius: 999,
    background: bg,
    color,
    border: border || '1px solid transparent',
    whiteSpace: 'nowrap',
});

export default function PricesPage() {
    const ctx = useCountry();
    const selectedCompanyId = ctx?.selectedCompanyId;

    const selectedCountryCode =
        ctx?.selectedCountryCode ||
        ctx?.countryCode ||
        ctx?.selectedCountry ||
        ctx?.country ||
        ctx?.selectedCountryIso ||
        '';

    const normalizedCountry = useMemo(() => {
        const c = String(selectedCountryCode || '').toUpperCase().trim();
        if (c === 'GE' || c === 'GEO' || c === 'GEORGIA') return 'GE';
        if (c === 'AL' || c === 'ALB' || c === 'ALBANIA') return 'AL';
        return '';
    }, [selectedCountryCode]);

    const PRICING_CURRENCY = 'USD';


    const [expandedNames, setExpandedNames] = useState(() => new Set());


    const [authMode, setAuthMode] = useState('userPool');

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [showDeleteFrame, setShowDeleteFrame] = useState(false);

    const [frames, setFrames] = useState([]);
    const [selectedFrameId, setSelectedFrameId] = useState('');
    const selectedFrame = useMemo(
        () => frames.find((f) => f.id === selectedFrameId) || null,
        [frames, selectedFrameId]
    );

    const [cars, setCars] = useState([]);

    // carId -> { id? (CarPrice.id), p1_2.. }
    const [priceMap, setPriceMap] = useState({});
    const originalPriceMapRef = useRef({});

    // create frame UI
    const [showNewFrame, setShowNewFrame] = useState(false);
    const [frameForm, setFrameForm] = useState({
        name: '',
        startDDMM: '',
        endDDMM: '',
    });

    // edit frame UI
    const [showEditFrame, setShowEditFrame] = useState(false);
    const [editFrameForm, setEditFrameForm] = useState({
        name: '',
        startDDMM: '',
        endDDMM: '',
    });

    const [addonsLoading, setAddonsLoading] = useState(false);
    const [addonsSaving, setAddonsSaving] = useState(false);
    const [addonsRow, setAddonsRow] = useState(null); // the db row (id etc.)
    const [addonsForm, setAddonsForm] = useState({
        insurancePerDay_1_5: '',
        insurancePerDay_6_plus: '',

        boosterSeatPerDay: '',
        boosterSeatMax: '',

        infantSeatPerDay: '',
        infantSeatMax: '',

        babySeatPerDay: '',
        babySeatMax: '',

        snowChainsPerDay: '',
        snowChainsMax: '',

        extraDriverPerDay: '',
        extraDriverMax: '',
    });

    const addonsOriginalRef = useRef(null);

    const addonsDirty = useMemo(() => {
        const o = addonsOriginalRef.current || null;
        if (!o) {
            return true; // new record => assume dirty once user is here
        }

        const { tiers, ok } = normalizeTiersForSave(addonsForm.insuranceTiers);
        const curTiersJson = ok ? JSON.stringify(tiers) : JSON.stringify(addonsForm.insuranceTiers || []);
        if (curTiersJson !== (o.insuranceTiersJson || '')) return true;

        const keys = [
            'boosterSeatPerDay',
            'boosterSeatMax',
            'infantSeatPerDay',
            'infantSeatMax',
            'babySeatPerDay',
            'babySeatMax',
            'snowChainsPerDay',
            'snowChainsMax',
            'extraDriverPerDay',
            'extraDriverMax',
        ];

        return keys.some((k) => {
            const cur = safeNum(addonsForm[k]);
            const orig = o[k] ?? null;
            return (cur ?? null) !== (orig ?? null);
        });
    }, [addonsForm]);

    const companyId = selectedCompanyId || null;

    useEffect(() => {
        (async () => {
            const m = await smartAuthMode();
            setAuthMode(m);
        })();
    }, []);

    useEffect(() => {
        setFrameForm({ name: '', startDDMM: '', endDDMM: '' });
        setShowNewFrame(false);

        setShowEditFrame(false);
        setEditFrameForm({ name: '', startDDMM: '', endDDMM: '' });
        setShowDeleteFrame(false);
        setAddonsRow(null);
        setAddonsForm({
            insurancePerDay: '',
            boosterSeatPerDay: '',
            infantSeatPerDay: '',
            babySeatPerDay: '',
            snowChainsPerDay: '',
            extraDriverPerDay: '',
        });
        addonsOriginalRef.current = null;
    }, [companyId]);
    useEffect(() => {
        loadAddonsPricing();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, normalizedCountry]);
    useEffect(() => {
        if (!selectedFrame) return;
        setEditFrameForm({
            name: (selectedFrame.name || '').trim(),
            startDDMM: isoToDDMM(selectedFrame.startDate),
            endDDMM: isoToDDMM(selectedFrame.endDate),
        });
    }, [selectedFrame]);

    async function loadAll() {
        if (!companyId) {
            setLoading(false);
            setFrames([]);
            setCars([]);
            setPriceMap({});
            originalPriceMapRef.current = {};
            setSelectedFrameId('');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const fr = await client.graphql({
                query: PriceFramesByCompanyByStart,
                variables: { CompanyId: companyId, sortDirection: 'ASC', limit: 1000 },
                authMode: 'userPool',
            });

            const frameItems = fr?.data?.priceFramesByCompanyByStart?.items || [];
            const cleanedFrames = frameItems
                .filter(Boolean)
                .sort((a, b) => compareDDMM(isoToDDMM(a.startDate), isoToDDMM(b.startDate)));
            setFrames(cleanedFrames);

            const today = todayDDMM();
            const containsToday = cleanedFrames.find((f) => {
                const s = isoToDDMM(f.startDate);
                const e = isoToDDMM(f.endDate);
                return ddmmInRange(today, s, e);
            });


            const fallback = cleanedFrames[cleanedFrames.length - 1] || null;
            const nextSelected = (containsToday || fallback)?.id || '';
            setSelectedFrameId((prev) => prev || nextSelected);

            // ---- load cars
            let allCars = [];
            let nextToken = null;
            do {
                const res = await client.graphql({
                    query: ListCars,
                    variables: {
                        limit: 1000,
                        nextToken,
                        filter: { CompanyId: { eq: companyId } },
                    },
                    authMode: 'userPool',
                });
                const data = res?.data?.listCars;
                allCars = allCars.concat((data?.items || []).filter(Boolean));
                nextToken = data?.nextToken || null;
            } while (nextToken);

            allCars.sort((a, b) => String(a.name).localeCompare(String(b.name)));
            setCars(allCars);
        } catch (e) {
            console.error(e);
            setError(e?.errors?.[0]?.message || e?.message || 'Failed to load pricing');
        } finally {
            setLoading(false);
        }
    }
    async function loadAddonsPricing() {
        if (!companyId || !normalizedCountry) {
            setAddonsRow(null);
            setAddonsForm({
                insuranceTiers: [{ minDays: 1, maxDays: null, perDay: '' }],
                boosterSeatPerDay: '',
                boosterSeatMax: '',
                infantSeatPerDay: '',
                infantSeatMax: '',
                babySeatPerDay: '',
                babySeatMax: '',
                snowChainsPerDay: '',
                snowChainsMax: '',
                extraDriverPerDay: '',
                extraDriverMax: '',
            });
            addonsOriginalRef.current = null;
            return;
        }

        setAddonsLoading(true);
        try {
            const res = await client.graphql({
                query: AddonsByCompanyByCountry,
                variables: {
                    CompanyId: companyId,
                    countryCode: { eq: normalizedCountry },
                    sortDirection: 'ASC',
                    limit: 1,
                },
                authMode: 'userPool',
            });

            const item = (res?.data?.addonsByCompanyByCountry?.items || []).filter(Boolean)[0] || null;
            setAddonsRow(item);

            // tiers: prefer insuranceTiers; fallback to legacy 2-tier
            const parsed = parseTiers(item?.insuranceTiers);
            const tiersFallback =
                item?.insurancePerDay_1_5 != null || item?.insurancePerDay_6_plus != null
                    ? [
                        { minDays: 1, maxDays: 5, perDay: item?.insurancePerDay_1_5 ?? 0 },
                        { minDays: 6, maxDays: null, perDay: item?.insurancePerDay_6_plus ?? 0 },
                    ]
                    : [{ minDays: 1, maxDays: null, perDay: '' }];

            const nextForm = {
                insuranceTiers: parsed || tiersFallback,

                boosterSeatPerDay: item?.boosterSeatPerDay ?? '',
                boosterSeatMax: item?.boosterSeatMax ?? '',

                infantSeatPerDay: item?.infantSeatPerDay ?? '',
                infantSeatMax: item?.infantSeatMax ?? '',

                babySeatPerDay: item?.babySeatPerDay ?? '',
                babySeatMax: item?.babySeatMax ?? '',

                snowChainsPerDay: item?.snowChainsPerDay ?? '',
                snowChainsMax: item?.snowChainsMax ?? '',

                extraDriverPerDay: item?.extraDriverPerDay ?? '',
                extraDriverMax: item?.extraDriverMax ?? '',
            };

            setAddonsForm(nextForm);

            // snapshot for dirty-check (store normalized JSON string for tiers)
            addonsOriginalRef.current = item
                ? {
                    insuranceTiersJson: JSON.stringify(parsed || tiersFallback),

                    boosterSeatPerDay: item?.boosterSeatPerDay ?? null,
                    boosterSeatMax: item?.boosterSeatMax ?? null,
                    infantSeatPerDay: item?.infantSeatPerDay ?? null,
                    infantSeatMax: item?.infantSeatMax ?? null,
                    babySeatPerDay: item?.babySeatPerDay ?? null,
                    babySeatMax: item?.babySeatMax ?? null,
                    snowChainsPerDay: item?.snowChainsPerDay ?? null,
                    snowChainsMax: item?.snowChainsMax ?? null,
                    extraDriverPerDay: item?.extraDriverPerDay ?? null,
                    extraDriverMax: item?.extraDriverMax ?? null,
                }
                : null;
        } catch (e) {
            console.error(e);
            setError(e?.errors?.[0]?.message || e?.message || 'Failed to load add-ons pricing');
        } finally {
            setAddonsLoading(false);
        }
    }
    async function saveAddonsPricing() {
        if (!companyId || !normalizedCountry) return;

        setAddonsSaving(true);
        setError(null);

        const norm = normalizeTiersForSave(addonsForm.insuranceTiers);
        if (!norm.ok) {
            setAddonsSaving(false);
            setError(norm.error || 'Invalid insurance tiers.');
            return;
        }

        const payload = {
            CompanyId: companyId,
            countryCode: normalizedCountry,
            currency: PRICING_CURRENCY,

            insuranceTiers: JSON.stringify(norm.tiers),

            // optional: keep legacy fields for old clients (derived from tiers if it matches)
            insurancePerDay_1_5: null,
            insurancePerDay_6_plus: null,

            boosterSeatPerDay: safeNum(addonsForm.boosterSeatPerDay),
            boosterSeatMax: safeNum(addonsForm.boosterSeatMax),

            infantSeatPerDay: safeNum(addonsForm.infantSeatPerDay),
            infantSeatMax: safeNum(addonsForm.infantSeatMax),

            babySeatPerDay: safeNum(addonsForm.babySeatPerDay),
            babySeatMax: safeNum(addonsForm.babySeatMax),

            snowChainsPerDay: safeNum(addonsForm.snowChainsPerDay),
            snowChainsMax: safeNum(addonsForm.snowChainsMax),

            extraDriverPerDay: safeNum(addonsForm.extraDriverPerDay),
            extraDriverMax: safeNum(addonsForm.extraDriverMax),
        };

        try {
            if (addonsRow?.id) {
                await client.graphql({
                    query: UpdateAddonsPricing,
                    variables: { input: { id: addonsRow.id, ...payload } },
                    authMode: 'userPool',
                });
            } else {
                const res = await client.graphql({
                    query: CreateAddonsPricing,
                    variables: { input: payload },
                    authMode: 'userPool',
                });
                const created = res?.data?.createAddonsPricing || null;
                setAddonsRow(created);
            }

            await loadAddonsPricing();
        } catch (e) {
            console.error(e);
            setError(e?.errors?.[0]?.message || e?.message || 'Failed to save add-ons pricing');
        } finally {
            setAddonsSaving(false);
        }
    }

    async function loadPricesForFrame(frameId) {
        if (!companyId || !frameId) {
            setPriceMap({});
            originalPriceMapRef.current = {};
            return;
        }

        setError(null);
        try {
            let all = [];
            let nextToken = null;
            do {
                const res = await client.graphql({
                    query: CarPricesByCompanyByFrame,
                    variables: {
                        CompanyId: companyId,
                        frameIdCarId: { beginsWith: { frameId } }, // ✅ only this frame
                        sortDirection: 'ASC',
                        limit: 1000,
                        nextToken,
                    },
                    authMode: 'userPool',
                });
                const data = res?.data?.carPricesByCompanyByFrame;
                all = all.concat((data?.items || []).filter(Boolean));
                nextToken = data?.nextToken || null;
            } while (nextToken);

            const m = {};
            for (const row of all) {
                m[row.carId] = {
                    id: row.id,
                    p1_2: row.p1_2 ?? null,
                    p3_4: row.p3_4 ?? null,
                    p5_10: row.p5_10 ?? null,
                    p11_20: row.p11_20 ?? null,
                    p20_plus: row.p20_plus ?? null,
                };
            }

            setPriceMap(m);
            originalPriceMapRef.current = JSON.parse(JSON.stringify(m));
        } catch (e) {
            console.error(e);
            setError(e?.errors?.[0]?.message || e?.message || 'Failed to load frame prices');
            setPriceMap({});
            originalPriceMapRef.current = {};
        }
    }

    useEffect(() => {
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId]);

    useEffect(() => {
        loadPricesForFrame(selectedFrameId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFrameId, companyId]);

    const dirtyCount = useMemo(() => {
        const orig = originalPriceMapRef.current || {};
        let dirty = 0;
        for (const c of cars) {
            const carId = c.id;
            const cur = priceMap[carId] || {};
            const o = orig[carId] || {};
            const changed = BUCKETS.some((k) => (cur[k] ?? null) !== (o[k] ?? null));
            if (changed) dirty += 1;
        }
        return dirty;
    }, [cars, priceMap]);

    function setCarBucket(carId, bucket, value) {
        setPriceMap((prev) => {
            const next = { ...prev };
            const row = { ...(next[carId] || {}) };
            row[bucket] = value;
            next[carId] = row;
            return next;
        });
    }
    function normName(v) {
        return String(v || '')
            .trim()
            .replace(/\s+/g, ' ')
            .toLowerCase();
    }

    const nameGroups = useMemo(() => {
        const m = new Map(); // normName -> { label, items }
        for (const c of cars) {
            const key = normName(c.name);
            const label = String(c.name || '').trim() || '—';
            if (!m.has(key)) m.set(key, { key, label, items: [] });
            m.get(key).items.push(c);
        }

        const arr = Array.from(m.values()).map((g) => ({
            ...g,
            items: [...g.items].sort((a, b) => String(a.plate || '').localeCompare(String(b.plate || ''))),
        }));

        // sort by display label
        arr.sort((a, b) => a.label.localeCompare(b.label));
        return arr;
    }, [cars]);

    function toggleNameGroup(key) {
        setExpandedNames((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }

    function setNameGroupBucket(groupCars, bucket, value) {
        setPriceMap((prev) => {
            const next = { ...prev };
            for (const c of groupCars) {
                const row = { ...(next[c.id] || {}) };
                row[bucket] = value;
                next[c.id] = row;
            }
            return next;
        });
    }

    // Show a single value if all cars in group match; otherwise blank ("mixed")
    function groupBucketDisplay(groupCars, bucket, priceMapLocal) {
        const norm = (v) => (v === null || v === undefined ? null : v);
        const vals = groupCars.map((c) => norm((priceMapLocal[c.id] || {})[bucket]));
        const first = vals[0] ?? null;
        const allSame = vals.every((v) => v === first);
        return allSame ? (first ?? '') : '';
    }

    async function saveAllPrices() {
        if (!companyId || !selectedFrameId) return;
        setSaving(true);
        setError(null);

        try {
            const orig = originalPriceMapRef.current || {};

            const changedCars = cars
                .map((c) => c.id)
                .filter((carId) => {
                    const cur = priceMap[carId] || {};
                    const o = orig[carId] || {};
                    return BUCKETS.some((k) => (cur[k] ?? null) !== (o[k] ?? null));
                });

            for (const carId of changedCars) {
                const cur = priceMap[carId] || {};
                const existingId = cur.id || (orig[carId] && orig[carId].id) || null;

                const payload = {
                    CompanyId: companyId,
                    frameId: selectedFrameId,
                    carId,
                    p1_2: cur.p1_2 ?? null,
                    p3_4: cur.p3_4 ?? null,
                    p5_10: cur.p5_10 ?? null,
                    p11_20: cur.p11_20 ?? null,
                    p20_plus: cur.p20_plus ?? null,
                };

                if (existingId) {
                    await client.graphql({
                        query: UpdateCarPrice,
                        variables: { input: { id: existingId, ...payload } },
                        authMode: 'userPool',
                    });
                } else {
                    const res = await client.graphql({
                        query: CreateCarPrice,
                        variables: { input: payload },
                        authMode: 'userPool',
                    });
                    const created = res?.data?.createCarPrice;
                    setPriceMap((prev) => ({
                        ...prev,
                        [carId]: { ...(prev[carId] || {}), id: created?.id },
                    }));
                }
            }

            await loadPricesForFrame(selectedFrameId);
        } catch (e) {
            console.error(e);
            setError(e?.errors?.[0]?.message || e?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    }

    async function createFrame() {
        if (!companyId) return;

        const startDDMM = (frameForm.startDDMM || '').trim();
        const endDDMM = (frameForm.endDDMM || '').trim();

        if (!startDDMM || !endDDMM) {
            setError('Start and end dates are required.');
            return;
        }

        const rawName = (frameForm.name || '').trim();
        const finalName = rawName || null;

        setSaving(true);
        setError(null);
        try {
            const res = await client.graphql({
                query: CreatePriceFrame,
                variables: {
                    input: {
                        CompanyId: companyId,
                        name: finalName,
                        startDate: ddmmToISO(startDDMM),
                        endDate: ddmmToISO(endDDMM),
                        currency: PRICING_CURRENCY
                    },
                },
                authMode: 'userPool',
            });

            const created = res?.data?.createPriceFrame;
            await loadAll();
            setSelectedFrameId(created?.id || '');
            setShowNewFrame(false);
            setFrameForm({ name: '', startDDMM: '', endDDMM: '' });
        } catch (e) {
            console.error(e);
            setError(e?.errors?.[0]?.message || e?.message || 'Failed to create frame');
        } finally {
            setSaving(false);
        }
    }

    async function saveFrameEdits() {
        if (!companyId || !selectedFrame) return;

        const startDDMM = (editFrameForm.startDDMM || '').trim();
        const endDDMM = (editFrameForm.endDDMM || '').trim();

        if (
            startDDMM.length !== 5 ||
            endDDMM.length !== 5 ||
            !startDDMM.includes('-') ||
            !endDDMM.includes('-')
        ) {
            setError('Start and end dates are required (DD-MM).');
            return;
        }

        const rawName = (editFrameForm.name || '').trim();
        const finalName = rawName || null;

        setSaving(true);
        setError(null);
        try {
            await client.graphql({
                query: UpdatePriceFrame,
                variables: {
                    input: {
                        id: selectedFrame.id,
                        name: finalName,
                        startDate: ddmmToISO(startDDMM),
                        endDate: ddmmToISO(endDDMM),
                        currency: PRICING_CURRENCY
                    },
                },
                authMode: 'userPool',
            });

            await loadAll();
            setShowEditFrame(false);
        } catch (e) {
            console.error(e);
            setError(e?.errors?.[0]?.message || e?.message || 'Failed to update frame');
        } finally {
            setSaving(false);
        }
    }

    async function deleteFrame() {
        if (!selectedFrame) return;

        setSaving(true);
        setError(null);
        setShowDeleteFrame(false);

        try {
            let all = [];
            let nextToken = null;
            do {
                const res = await client.graphql({
                    query: CarPricesByCompanyByFrame,
                    variables: {
                        CompanyId: companyId,
                        frameIdCarId: { beginsWith: { frameId: selectedFrame.id } },
                        limit: 1000,
                        nextToken,
                    },
                    authMode: 'userPool',
                });
                const data = res?.data?.carPricesByCompanyByFrame;
                all = all.concat((data?.items || []).filter(Boolean));
                nextToken = data?.nextToken || null;
            } while (nextToken);

            for (const row of all) {
                await client.graphql({
                    query: DeleteCarPrice,
                    variables: { input: { id: row.id } },
                    authMode: 'userPool',
                });
            }

            await client.graphql({
                query: DeletePriceFrame,
                variables: { input: { id: selectedFrame.id } },
                authMode: 'userPool',
            });

            setSelectedFrameId('');
            setShowEditFrame(false);
            await loadAll();
        } catch (e) {
            console.error(e);
            setError(e?.errors?.[0]?.message || e?.message || 'Failed to delete frame');
        } finally {
            setSaving(false);
        }
    }

    function DDMMPicker({ labelText, value, onChange }) {
        const v = String(value || '');
        const [dRaw, mRaw] = v.includes('-') ? v.split('-') : ['', ''];
        const dd = dRaw || '';
        const mm = mRaw || '';

        const maxDays = mm ? daysInMonth(mm) : 31;
        const days = Array.from({ length: maxDays }, (_, i) => pad2(i + 1));
        const months = Array.from({ length: 12 }, (_, i) => pad2(i + 1));

        const d = dd && Number(dd) <= maxDays ? dd : '';

        return (
            <div>
                <div style={labelStyle}>{labelText}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <select
                        style={inputStyle}
                        value={d}
                        onChange={(e) => {
                            const nextDD = e.target.value;
                            onChange(nextDD ? `${nextDD}-` : '');
                        }}
                    >
                        <option value="">DD</option>
                        {days.map((x) => (
                            <option key={x} value={x}>
                                {x}
                            </option>
                        ))}
                    </select>

                    <select
                        style={inputStyle}
                        value={mm}
                        onChange={(e) => {
                            const nextMM = e.target.value;
                            if (!d) return;
                            onChange(nextMM ? `${d}-${nextMM}` : `${d}-`);
                        }}
                        disabled={!d}
                    >
                        <option value="">{d ? 'MM' : 'MM'}</option>
                        {months.map((x) => (
                            <option key={x} value={x}>
                                {x}
                            </option>
                        ))}
                    </select>
                </div>

                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>
                    Required. Recurs yearly (DD-MM).
                </div>
            </div>
        );
    }


    const selectedFrameLabel = useMemo(() => {
        if (!selectedFrame) return '';
        const s = isoToDDMM(selectedFrame.startDate);
        const e = isoToDDMM(selectedFrame.endDate);
        const n = (selectedFrame.name || '').trim();
        return n || defaultFrameLabel(s, e);
    }, [selectedFrame]);

    const createDisabled = useMemo(() => {
        if (!companyId || saving) return true;
        const s = (frameForm.startDDMM || '').trim();
        const e = (frameForm.endDDMM || '').trim();
        if (s.length !== 5 || e.length !== 5 || !s.includes('-') || !e.includes('-')) return true;
        const [sd, sm] = s.split('-');
        const [ed, em] = e.split('-');
        if (!sd || !sm || !ed || !em) return true;

        return false;
    }, [companyId, saving, frameForm.startDDMM, frameForm.endDDMM]);

    const editDisabled = useMemo(() => {
        if (!companyId || saving || !selectedFrame) return true;
        const s = (editFrameForm.startDDMM || '').trim();
        const e = (editFrameForm.endDDMM || '').trim();
        if (s.length !== 5 || e.length !== 5 || !s.includes('-') || !e.includes('-')) return true;
        const [sd, sm] = s.split('-');
        const [ed, em] = e.split('-');
        if (!sd || !sm || !ed || !em) return true;

        return false;
    }, [companyId, saving, selectedFrame, editFrameForm.startDDMM, editFrameForm.endDDMM]);

    const statusPill = useMemo(() => {
        if (loading) return pillStyle('#e5e7eb', '#374151', '1px solid #d1d5db');
        if (saving) return pillStyle('#e0f2fe', '#075985', '1px solid #bae6fd');
        if (!companyId) return pillStyle('#fee2e2', '#991b1b', '1px solid #fecaca');
        if (dirtyCount > 0) return pillStyle('#fef9c3', '#854d0e', '1px solid #facc15');
        return pillStyle('#dcfce7', '#166534', '1px solid #bbf7d0');
    }, [loading, saving, companyId, dirtyCount]);

    const statusText = useMemo(() => {
        if (loading) return 'Loading';
        if (saving) return 'Saving';
        if (!companyId) return 'No company';
        if (dirtyCount > 0) return `${dirtyCount} unsaved`;
        return 'Up to date';
    }, [loading, saving, companyId, dirtyCount]);

    return (
        <div style={{ background: '#f3f4f6', minHeight: 'calc(100vh - 64px)', position: 'relative' }}>
            <style>{`
        .prices-grid {
          display: grid;
          gap: 18px;
        }

        .prices-top {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          gap: 12px;
        }

        .prices-actions {
          display: flex;
          gap: 8px;
          align-items: center;
          flex-wrap: wrap;
        }

        .frame-row {
          display: grid;
          grid-template-columns: 1.4fr 0.6fr auto;
          gap: 12px;
          align-items: end;
        }

        .frame-buttons {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
          flex-wrap: wrap;
        }

        .table-wrap {
          overflow: auto;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          background: #fff;
        }

        table.prices-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          min-width: 760px;
          font-size: 13px;
        }

        table.prices-table thead th {
          text-align: left;
          font-size: 12px;
          color: #374151;
          padding: 10px 10px;
          border-bottom: 1px solid #e5e7eb;
          position: sticky;
          top: 0;
          background: #ffffff;
          z-index: 1;
        }

        table.prices-table tbody td {
          padding: 10px;
          border-bottom: 1px solid #f1f5f9;
          vertical-align: middle;
        }

        .bottom-save {
            display: block;
            position: sticky;
            bottom: 0;
            margin-top: 12px;
            padding-top: 10px;
            background: rgba(243,244,246,0.92);
            backdrop-filter: blur(6px);
            border-top: 1px solid #e5e7eb;
        }

        @media (max-width: 840px) {
          .prices-top {
            flex-direction: column;
            align-items: stretch;
          }
        }

        @media (max-width: 860px) {
          .frame-row {
            grid-template-columns: 1fr;
          }
          .frame-buttons {
            justify-content: flex-start;
          }
        }

        @media (max-width: 720px) {
          .bottom-save-inner {
            display: flex;
            gap: 10px;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
          }
          .table-wrap {
            max-height: none !important;
          }
        }
          /* --- responsive grids (addons + frame forms) --- */

.addons-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.frame-edit-grid {
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 0.7fr;
  gap: 10px;
  align-items: start;
}

@media (max-width: 860px) {
  .addons-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .frame-edit-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 520px) {
  .addons-grid {
    grid-template-columns: 1fr;
  }
}

/* --- table tweaks on small screens --- */

@media (max-width: 520px) {
  table.prices-table {
    min-width: 640px; /* still scrolls, but less painful */
  }
  table.prices-table thead th,
  table.prices-table tbody td {
    padding: 8px;
  }
}
/* Mobile: show cards instead of the wide table */
.mobile-only { display: none; }
.desktop-only { display: block; }

@media (max-width: 720px) {
  .mobile-only { display: block; }
  .desktop-only { display: none; }
}

.price-cards {
  display: grid;
  gap: 10px;
}

.price-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  padding: 12px;
}

.price-card-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
}

.price-card-grid {
  margin-top: 10px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}

      `}</style>

            <div style={{ maxWidth: 1100, margin: '0 auto', padding: '16px 12px 32px' }} className="prices-grid">
                {/* Header (match CarsPage style) */}
                <header className="prices-top">
                    <div>
                        <h3 style={{ margin: 0, fontSize: 22 }}>Pricing</h3>
                        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
                            Define seasonal frames and set per-car prices for day ranges.
                        </p>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <span style={statusPill}>{statusText}</span>

                        <div className="prices-actions">
                            <Button onClick={loadAll} disabled={loading || saving} style={{ background: 'rgb(243, 244, 246)' }}>
                                <RefreshCcw size={16} style={{ marginRight: 6 }} />
                                Refresh
                            </Button>
                        </div>
                    </div>
                </header>

                {/* Error / missing company */}
                {error && (
                    <div
                        style={{
                            borderRadius: 12,
                            padding: '10px 12px',
                            border: '1px solid #fecaca',
                            background: '#fef2f2',
                            color: '#991b1b',
                            fontSize: 13,
                        }}
                    >
                        {error}
                    </div>
                )}

                {!companyId && (
                    <section style={cardStyle()}>
                        <div style={{ fontSize: 13, color: '#374151' }}>
                            <b>No company selected.</b>
                            <div style={{ marginTop: 6, color: '#6b7280' }}>
                                Pick a company in the country selector before editing frames and prices.
                            </div>
                        </div>
                    </section>
                )}
                {/* Add-ons pricing (per country) */}
                <section style={cardStyle()}>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                        <div>
                            <div style={{ fontSize: 14, fontWeight: 900, color: '#111827' }}>Add-ons pricing</div>
                            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                                Per-day insurance & extras for <b>{normalizedCountry || '—'}</b> (currency: <b>{PRICING_CURRENCY}</b>).
                            </div>
                            {normalizedCountry === 'GE' ? (
                                <div style={{ fontSize: 12, color: '#92400e', marginTop: 6, maxWidth: 760 }}>
                                    Georgia reservation insurance is fixed by car body in the quote flow: sedan/hatchback <b>$7/day</b>, SUV <b>$10/day</b>, van/minivan <b>$15/day</b>. The tiers below are fallback-only if a car is missing a body type.
                                </div>
                            ) : null}
                        </div>

                        <Button
                            onClick={saveAddonsPricing}
                            disabled={!companyId || !normalizedCountry || addonsSaving || addonsLoading || !addonsDirty}
                        >
                            <Save size={16} style={{ marginRight: 6 }} />
                            Save add-ons
                        </Button>
                    </div>

                    {!companyId || !normalizedCountry ? (
                        <div style={{ marginTop: 10, fontSize: 13, color: '#6b7280' }}>
                            Select a valid country (GE/AL) and company to edit add-ons.
                        </div>
                    ) : (
                        <div style={{ marginTop: 12 }}>
                            {/* Insurance tiers */}
                            <div style={{ fontSize: 12, fontWeight: 900, color: '#111827', marginBottom: 8 }}>
                                {normalizedCountry === 'GE'
                                    ? 'Insurance tiers (Georgia fallback only)'
                                    : 'Insurance tiers (per day, based on reservation length)'}
                            </div>

                            <div style={{ display: 'grid', gap: 10, marginBottom: 14 }}>
                                {(addonsForm.insuranceTiers || []).map((t, idx) => (
                                    <div
                                        key={idx}
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 1fr 1.2fr auto',
                                            gap: 10,
                                            alignItems: 'end',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: 12,
                                            padding: 10,
                                            background: '#fff',
                                        }}
                                    >
                                        <div>
                                            <div style={labelStyle}>Min days</div>
                                            <input
                                                style={inputStyle}
                                                inputMode="numeric"
                                                placeholder="1"
                                                value={t.minDays ?? ''}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    setAddonsForm((p) => {
                                                        const next = [...(p.insuranceTiers || [])];
                                                        next[idx] = { ...next[idx], minDays: v };
                                                        return { ...p, insuranceTiers: next };
                                                    });
                                                }}
                                                disabled={addonsLoading || addonsSaving}
                                            />
                                        </div>

                                        <div>
                                            <div style={labelStyle}>Max days (blank = 9+)</div>
                                            <input
                                                style={inputStyle}
                                                inputMode="numeric"
                                                placeholder="(open)"
                                                value={t.maxDays ?? ''}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    setAddonsForm((p) => {
                                                        const next = [...(p.insuranceTiers || [])];
                                                        next[idx] = { ...next[idx], maxDays: v === '' ? null : v };
                                                        return { ...p, insuranceTiers: next };
                                                    });
                                                }}
                                                disabled={addonsLoading || addonsSaving}
                                            />
                                        </div>

                                        <div>
                                            <div style={labelStyle}>Price per day</div>
                                            <input
                                                style={inputStyle}
                                                inputMode="decimal"
                                                placeholder="—"
                                                value={t.perDay ?? ''}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    setAddonsForm((p) => {
                                                        const next = [...(p.insuranceTiers || [])];
                                                        next[idx] = { ...next[idx], perDay: v };
                                                        return { ...p, insuranceTiers: next };
                                                    });
                                                }}
                                                disabled={addonsLoading || addonsSaving}
                                            />
                                        </div>

                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <Button
                                                onClick={() => {
                                                    setAddonsForm((p) => {
                                                        const next = [...(p.insuranceTiers || [])];
                                                        next.splice(idx, 1);
                                                        return { ...p, insuranceTiers: next.length ? next : [{ minDays: 1, maxDays: null, perDay: '' }] };
                                                    });
                                                }}
                                                disabled={addonsLoading || addonsSaving}
                                                style={{ background: 'white' }}
                                            >
                                                <Trash2 size={16} />
                                            </Button>
                                        </div>
                                    </div>
                                ))}

                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    <Button
                                        onClick={() => {
                                            setAddonsForm((p) => ({
                                                ...p,
                                                insuranceTiers: [...(p.insuranceTiers || []), { minDays: '', maxDays: null, perDay: '' }],
                                            }));
                                        }}
                                        disabled={addonsLoading || addonsSaving}
                                        style={{ background: 'rgb(243, 244, 246)' }}
                                    >
                                        <Plus size={16} style={{ marginRight: 6 }} />
                                        Add tier
                                    </Button>

                                    <div style={{ fontSize: 12, color: '#6b7280', alignSelf: 'center' }}>
                                        Example: 1–3, 4–8, 9+ (set last max blank).
                                    </div>
                                </div>

                                {/* Inline validation preview */}
                                {(() => {
                                    const v = normalizeTiersForSave(addonsForm.insuranceTiers);
                                    if (v.ok) return null;
                                    return (
                                        <div style={{ fontSize: 12, color: '#b91c1c' }}>
                                            {v.error}
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Extras with caps */}
                            <div style={{ fontSize: 12, fontWeight: 900, color: '#111827', marginBottom: 8 }}>
                                Extras (per day + max cap)
                            </div>

                            <div className="addons-grid">
                                {[
                                    ['extraDriver', 'Extra driver (each)'],
                                    ['snowChains', 'Snow chains'],
                                    ['boosterSeat', 'Booster seat'],
                                    ['infantSeat', 'Infant seat'],
                                    ['babySeat', 'Baby seat'],
                                ].flatMap(([k, title]) => [
                                    [`${k}PerDay`, `${title} (per day)`],
                                    [`${k}Max`, `${title} (max total)`],
                                ]).map(([key, label]) => (
                                    <div key={key}>
                                        <div style={labelStyle}>{label}</div>
                                        <input
                                            style={inputStyle}
                                            inputMode="decimal"
                                            placeholder="—"
                                            value={addonsForm[key]}
                                            onChange={(e) => setAddonsForm((p) => ({ ...p, [key]: e.target.value }))}
                                            disabled={addonsLoading || addonsSaving}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                    )}

                    {(addonsLoading || addonsSaving) && (
                        <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280' }}>
                            {addonsLoading ? 'Loading add-ons…' : 'Saving add-ons…'}
                        </div>
                    )}

                    <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280' }}>
                        These values should be used in your public quote breakdown (total extras = sum(selected extras) × days).
                    </div>

                </section>

                {/* Frame controls */}
                <section style={cardStyle()}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                        <div style={{ fontSize: 14, fontWeight: 900, color: '#111827' }}>Price frames</div>

                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <Button onClick={() => setShowNewFrame((v) => !v)} disabled={!companyId || saving}>
                                <Plus size={16} style={{ marginRight: 6 }} />
                                New frame
                            </Button>

                            <Button onClick={saveAllPrices} disabled={!companyId || !selectedFrameId || saving || dirtyCount === 0}>
                                <Save size={16} style={{ marginRight: 6 }} />
                                Save prices {dirtyCount > 0 ? `(${dirtyCount})` : ''}
                            </Button>
                        </div>
                    </div>

                    <div className="frame-row">
                        <div>
                            <div style={labelStyle}>Price frame</div>
                            <select
                                value={selectedFrameId}
                                onChange={(e) => {
                                    setSelectedFrameId(e.target.value);
                                    setShowEditFrame(false);
                                }}
                                style={inputStyle}
                                disabled={!companyId || loading || saving}
                            >
                                <option value="">Select a frame…</option>
                                {frames.map((f) => {
                                    const s = isoToDDMM(f.startDate);
                                    const e = isoToDDMM(f.endDate);
                                    const n = (f.name || '').trim() || defaultFrameLabel(s, e);
                                    return (
                                        <option key={f.id} value={f.id}>
                                            {n} ({s} → {e})
                                        </option>
                                    );
                                })}
                            </select>

                        </div>

                        <div>
                            <div style={labelStyle}>Currency</div>
                            <div
                                style={{
                                    padding: '8px 10px',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 8,
                                    background: '#f9fafb',
                                    fontSize: 13,
                                    color: '#111827',
                                }}
                            >
                                {PRICING_CURRENCY}
                            </div>
                        </div>

                        <div className="frame-buttons">
                            <Button onClick={() => setShowEditFrame((v) => !v)} disabled={!selectedFrameId || saving}>
                                <Pencil size={16} style={{ marginRight: 6 }} />
                                Edit frame
                            </Button>

                            <Button onClick={() => setShowDeleteFrame(true)} disabled={!selectedFrameId || saving}>
                                <Trash2 size={16} style={{ marginRight: 6 }} />
                                Delete
                            </Button>
                        </div>
                    </div>
                    {showDeleteFrame && selectedFrame && (
                        <div
                            onClick={() => !saving && setShowDeleteFrame(false)}
                            style={{
                                position: 'fixed',
                                inset: 0,
                                background: 'rgba(0,0,0,0.35)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 50,
                                padding: 12,
                            }}
                        >
                            <div
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                    width: 'min(520px, 100%)',
                                    background: '#fff',
                                    borderRadius: 14,
                                    border: '1px solid #e5e7eb',
                                    boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                                    padding: 14,
                                }}
                            >
                                <div style={{ fontSize: 14, fontWeight: 900, color: '#111827' }}>
                                    Delete price frame?
                                </div>

                                <div style={{ marginTop: 8, fontSize: 13, color: '#374151', lineHeight: 1.45 }}>
                                    This will permanently delete:
                                    <ul style={{ margin: '8px 0 0 18px', color: '#374151' }}>
                                        <li>The frame <b>{selectedFrameLabel}</b></li>
                                        <li>All car prices inside that frame</li>
                                    </ul>
                                    <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
                                        This cannot be undone.
                                    </div>
                                </div>

                                <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                    <Button
                                        onClick={() => setShowDeleteFrame(false)}
                                        disabled={saving}
                                        style={{ background: 'white' }}
                                    >
                                        Cancel
                                    </Button>

                                    <Button
                                        onClick={async () => {
                                            await deleteFrame();
                                            setShowDeleteFrame(false);
                                        }}
                                        disabled={saving}
                                        style={{ background: '#ef4444' }}
                                    >
                                        <Trash2 size={16} style={{ marginRight: 6 }} />
                                        Delete permanently
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {showEditFrame && selectedFrame && (
                        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #e5e7eb' }}>
                            <div style={{ fontSize: 12, color: '#111827', fontWeight: 800, marginBottom: 10 }}>
                                Editing: {selectedFrameLabel}
                            </div>

                            <div className="frame-edit-grid">
                                <div>
                                    <div style={labelStyle}>
                                        Name <span style={{ fontWeight: 500, color: '#6b7280' }}>(optional)</span>
                                    </div>
                                    <input
                                        style={inputStyle}
                                        value={editFrameForm.name}
                                        onChange={(e) => setEditFrameForm((p) => ({ ...p, name: e.target.value }))}
                                        placeholder="Leave empty to auto-label by dates"
                                    />
                                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>
                                        Display label:{' '}
                                        <span style={{ color: '#111827', fontWeight: 700 }}>
                                            {(editFrameForm.name || '').trim() ||
                                                defaultFrameLabel(
                                                    (editFrameForm.startDDMM || '').slice(0, 5),
                                                    (editFrameForm.endDDMM || '').slice(0, 5)
                                                )}
                                        </span>
                                    </div>

                                </div>

                                <DDMMPicker
                                    labelText="Start (required)"
                                    value={editFrameForm.startDDMM}
                                    onChange={(v) => setEditFrameForm((p) => ({ ...p, startDDMM: v }))}
                                />

                                <DDMMPicker
                                    labelText="End (required)"
                                    value={editFrameForm.endDDMM}
                                    onChange={(v) => setEditFrameForm((p) => ({ ...p, endDDMM: v }))}
                                />

                                <div>
                                    <div style={labelStyle}>Currency (forced)</div>
                                    <div style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                                        {PRICING_CURRENCY}
                                    </div>
                                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>All pricing is in USD.</div>
                                </div>
                            </div>

                            <div style={{ marginTop: 10, display: 'flex', gap: 8, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                <div style={{ fontSize: 12, color: '#6b7280' }}>
                                    Dates are required. Wrap-around is allowed (e.g. 15-11 → 10-02).
                                </div>

                                <div style={{ display: 'flex', gap: 8 }}>
                                    <Button onClick={() => setShowEditFrame(false)} disabled={saving} style={{ background: 'white' }}>
                                        Cancel
                                    </Button>
                                    <Button onClick={saveFrameEdits} disabled={editDisabled}>
                                        Save frame
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {showNewFrame && (
                        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #e5e7eb' }}>
                            <div className="frame-edit-grid">
                                <div>
                                    <div style={labelStyle}>
                                        Name <span style={{ fontWeight: 500, color: '#6b7280' }}>(optional)</span>
                                    </div>
                                    <input
                                        style={inputStyle}
                                        value={frameForm.name}
                                        onChange={(e) => setFrameForm((p) => ({ ...p, name: e.target.value }))}
                                        placeholder="Leave empty to auto-label by dates"
                                    />
                                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>
                                        Display label:{' '}
                                        <span style={{ color: '#111827', fontWeight: 700 }}>
                                            {(frameForm.name || '').trim() ||
                                                defaultFrameLabel(
                                                    (frameForm.startDDMM || '').slice(0, 5),
                                                    (frameForm.endDDMM || '').slice(0, 5)
                                                )}
                                        </span>
                                    </div>
                                </div>

                                <DDMMPicker
                                    labelText="Start (required)"
                                    value={frameForm.startDDMM}
                                    onChange={(v) => setFrameForm((p) => ({ ...p, startDDMM: v }))}
                                />

                                <DDMMPicker
                                    labelText="End (required)"
                                    value={frameForm.endDDMM}
                                    onChange={(v) => setFrameForm((p) => ({ ...p, endDDMM: v }))}
                                />

                                <div>
                                    <div style={labelStyle}>Currency (forced)</div>
                                    <div style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                                        {PRICING_CURRENCY}
                                    </div>
                                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>All pricing is in USD.</div>
                                </div>
                            </div>

                            <div style={{ marginTop: 10, display: 'flex', gap: 8, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                <div style={{ fontSize: 12, color: '#6b7280' }}>
                                    Dates are required. Frame length can be anything. Wrap-around is allowed (e.g. 11-15 → 02-10).
                                </div>

                                <div style={{ display: 'flex', gap: 8 }}>
                                    <Button onClick={() => setShowNewFrame(false)} disabled={saving} style={{ background: 'white' }}>
                                        Cancel
                                    </Button>
                                    <Button onClick={createFrame} disabled={createDisabled}>
                                        Create frame
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </section>

                {/* Prices table */}
                <section style={cardStyle()}>
                    {!selectedFrameId ? (
                        <div style={{ color: '#6b7280', fontSize: 13 }}>Select a frame to edit prices.</div>
                    ) : (
                        <>
                            {/* DESKTOP: table */}
                            <div className="desktop-only">
                                <div className="table-wrap" style={{ maxHeight: '70vh' }}>
                                    <table className="prices-table">
                                        <thead>
                                            <tr>
                                                <th style={{ minWidth: 240 }}>Car</th>
                                                {BUCKETS.map((b) => (
                                                    <th key={b} style={{ minWidth: 140 }}>
                                                        {rangesLabel(b)}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {nameGroups.map((g) => {
                                                const isOpen = expandedNames.has(g.key);
                                                const groupLabel = `${g.label} (${g.items.length})`;

                                                return (
                                                    <React.Fragment key={g.key}>
                                                        {/* Group row (bulk edit for identical names) */}
                                                        <tr
                                                            onClick={() => {
                                                                if (g.items.length > 1) toggleNameGroup(g.key);
                                                            }}
                                                            style={{ cursor: g.items.length > 1 ? 'pointer' : 'default', background: '#f9fafb' }}
                                                            title={g.items.length > 1 ? 'Click to expand/collapse' : ''}
                                                        >
                                                            <td>
                                                                <div style={{ fontWeight: 900, color: '#111827', display: 'flex', gap: 8, alignItems: 'center' }}>
                                                                    <span style={{ width: 18, display: 'inline-block', textAlign: 'center' }}>
                                                                        {g.items.length > 1 ? (isOpen ? '▾' : '▸') : '•'}
                                                                    </span>
                                                                    <span>{groupLabel}</span>
                                                                </div>

                                                                {g.items.length > 1 ? (
                                                                    <div style={{ fontSize: 12, color: '#6b7280', marginLeft: 26 }}>
                                                                        Editing a bucket here applies to all cars with this name. Click to show individual cars.
                                                                    </div>
                                                                ) : (
                                                                    <div style={{ fontSize: 12, color: '#6b7280', marginLeft: 26 }}>
                                                                        Single car
                                                                    </div>
                                                                )}
                                                            </td>

                                                            {BUCKETS.map((b) => {
                                                                const display = groupBucketDisplay(g.items, b, priceMap);
                                                                return (
                                                                    <td key={b} onClick={(e) => e.stopPropagation()}>
                                                                        <input
                                                                            style={inputStyle}
                                                                            inputMode="decimal"
                                                                            placeholder="—"
                                                                            value={display}
                                                                            onChange={(e) => setNameGroupBucket(g.items, b, safeNum(e.target.value))}
                                                                        />
                                                                        {g.items.length > 1 && (
                                                                            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                                                                                {display === '' ? 'mixed' : 'all'}
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>

                                                        {/* Expanded individual cars */}
                                                        {isOpen &&
                                                            g.items.map((c) => {
                                                                const row = priceMap[c.id] || {};
                                                                return (
                                                                    <tr key={c.id}>
                                                                        <td style={{ paddingLeft: 28 }}>
                                                                            <div style={{ fontWeight: 700, color: '#111827' }}>{c.name}</div>
                                                                            <div style={{ fontSize: 12, color: '#6b7280' }}>
                                                                                {c.plate} {c.bodyType ? `• ${c.bodyType}` : ''} {c.fuelType ? `• ${c.fuelType}` : ''}{' '}
                                                                                {c.transmission ? `• ${c.transmission}` : ''}
                                                                            </div>
                                                                        </td>

                                                                        {BUCKETS.map((b) => (
                                                                            <td key={b}>
                                                                                <input
                                                                                    style={inputStyle}
                                                                                    inputMode="decimal"
                                                                                    placeholder="—"
                                                                                    value={row[b] ?? ''}
                                                                                    onChange={(e) => setCarBucket(c.id, b, safeNum(e.target.value))}
                                                                                />
                                                                            </td>
                                                                        ))}
                                                                    </tr>
                                                                );
                                                            })}
                                                    </React.Fragment>
                                                );
                                            })}

                                            {cars.length === 0 && (
                                                <tr>
                                                    <td colSpan={1 + BUCKETS.length} style={{ padding: 10, color: '#6b7280' }}>
                                                        No cars found for this company.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>

                                    </table>
                                </div>
                            </div>

                            {/* MOBILE: cards */}
                            <div className="mobile-only">
                                <div className="price-cards">
                                    {cars.map((c) => {
                                        const row = priceMap[c.id] || {};
                                        return (
                                            <div key={c.id} className="price-card">
                                                <div className="price-card-head">
                                                    <div>
                                                        <div style={{ fontWeight: 800, color: '#111827' }}>{c.name}</div>
                                                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                                                            {c.plate} {c.bodyType ? `• ${c.bodyType}` : ''}  {c.fuelType ? `• ${c.fuelType}` : ''} {c.transmission ? `• ${c.transmission}` : ''}
                                                        </div>
                                                    </div>
                                                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                                                        {PRICING_CURRENCY}
                                                    </div>
                                                </div>

                                                <div className="price-card-grid">
                                                    {BUCKETS.map((b) => (
                                                        <div key={b}>
                                                            <div style={labelStyle}>{rangesLabel(b)}</div>
                                                            <input
                                                                style={inputStyle}
                                                                inputMode="decimal"
                                                                placeholder="—"
                                                                value={row[b] ?? ''}
                                                                onChange={(e) => setCarBucket(c.id, b, safeNum(e.target.value))}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {cars.length === 0 && (
                                        <div style={{ padding: 10, color: '#6b7280', fontSize: 13 }}>
                                            No cars found for this company.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}


                    <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280' }}>
                        Tip: leave a cell empty to mean “no price set”. Your public booking flow should refuse to quote if any required bucket is missing.
                    </div>

                    {/* Bottom save (mobile) */}
                    <div className="bottom-save">
                        <div className="bottom-save-inner">
                            <div style={{ fontSize: 12, color: '#6b7280' }}>
                                {dirtyCount > 0 ? `${dirtyCount} car(s) have unsaved changes.` : 'No unsaved changes.'}
                            </div>

                            <Button onClick={saveAllPrices} disabled={!companyId || !selectedFrameId || saving || dirtyCount === 0}>
                                <Save size={16} style={{ marginRight: 6 }} />
                                Save {dirtyCount > 0 ? `(${dirtyCount})` : ''}
                            </Button>
                        </div>
                    </div>
                </section>

                {(loading || saving) && (
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{loading ? 'Loading…' : 'Saving…'}</div>
                )}
            </div>
        </div>
    );
}
